import {
    Autocomplete,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    TextField,
    Typography,
    createFilterOptions,
} from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
import YmalTable from "./YmalTable";
import { LIVE, SCHEDULED_STATUS } from "../../LandingPage/constants";
import { useFormik } from "formik";
import * as Yup from "yup";
import { DateTimePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import axios from "../../../../utils/axiosInstance";
import { enqueueSnackbar } from "notistack";
import EastIcon from "@mui/icons-material/East";
import {
    ACTION,
    PERMISSIONS,
    usePermissions,
} from "../../../../utils/permissions";

const ContentCurationVersions = () => {
    const scheduleRef = useRef<any>(null);
    const navigate = useNavigate();
    const { hasPermission } = usePermissions();
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [assets, setAssets] = useState<any>([]);
    const [videoAPILoader, setVideoAPILoader] = useState(false);
    const [playlistAPILoader, setPlaylistAPILoader] = useState(false);
    const [playlists, setPlaylists] = useState<any>([]);
    const [saveLoader, setSaveLoader] = useState(false);
    const initialState = {
        publishDateTime: null,
        assetId: null,
        assetRef: null,
        assetTitle: null,
        playlistId: null,
        playlistTitle: null,
        contentType: "",
    };

    const {
        values,
        touched,
        errors,
        handleBlur,
        handleSubmit,
        handleChange,
        setFieldValue,
        handleReset,
        setValues,
        resetForm,
        setFieldTouched,
    } = useFormik({
        initialValues: initialState,
        validationSchema: Yup.object({
            publishDateTime: Yup.date()
                .min(
                    dayjs().add(10, "minutes"),
                    "Publish date and time must be at least 10 minutes from now"
                )
                .required("Publish date and time is required"),
            assetId: Yup.string().required("Content ID is required"),
            assetRef: Yup.string().required("ReferenceId is required"),
            playlistId: Yup.string().required("Playlist ID is required"),
        }),
        onSubmit: (values) => {
            createYMALMapping(values);
        },
    });

    useEffect(() => {
        getVideosAPI();
        getPlayListAPI();
    }, []);

    const getVideosAPI = async () => {
        try {
            setVideoAPILoader(true);
            const videoInfo = await axios.get(
                `${process.env.BASE_URL}api/v2/cms/videos/all`
            );
            if (videoInfo.data?.success) {
                setAssets(videoInfo.data?.data);
            } else {
                enqueueSnackbar(videoInfo.data?.message, { variant: "error" });
            }
        } catch (error) {
            console.log("Error in getting master information", error);
        } finally {
            setVideoAPILoader(false);
        }
    };

    const getPlayListAPI = async () => {
        try {
            setPlaylistAPILoader(true);
            const materInformation = await axios.get(
                `${process.env.BASE_URL}api/v1/cms/playlist/fetch`
            );
            if (materInformation.data?.success) {
                setPlaylists(materInformation.data?.data);
            } else {
                enqueueSnackbar(materInformation.data?.message, {
                    variant: "error",
                });
            }
        } catch (error) {
            console.log("Error in getting master information", error);
        } finally {
            setPlaylistAPILoader(false);
        }
    };

    const closeModal = () => {
        resetForm();
        setShowCreateModal(false);
    };

    const createYMALMapping = async (values: any) => {
        try {
            setSaveLoader(true);
            const payload = {
                ...values,
                assetId: parseInt(values.assetId),
            };
            const response = await axios.post(
                `${process.env.BASE_URL}api/v1/cms/ymal`,
                payload
            );
            setSaveLoader(false);
            if (response.data?.success) {
                closeModal();
                scheduleRef.current.getMappingList();
                enqueueSnackbar(
                    "Successfully Scheduled You May Also Like Rail.",
                    { variant: "success" }
                );
            } else {
                enqueueSnackbar(response.data.message, { variant: "error" });
            }
        } catch (error: any) {
            setSaveLoader(false);
            enqueueSnackbar(error.response?.data?.message, {
                variant: "warning",
            });
        }
    };

    const filterOptions = createFilterOptions({
        matchFrom: "any",
        stringify: (option: any) => option.name + option.id,
    });

    const renderOptionsInitial = (options: any) => {
        if (typeof options == "string") {
            const filterVideo = assets.filter(
                (list: any) => list.id === options
            );
            if (filterVideo.length > 0) {
                return filterVideo[0].name + " - " + filterVideo[0].id;
            } else {
                return "";
            }
        } else if (typeof options == "object") {
            return options.name + " - " + options.id;
        }
    };

    const renderOptionsInitialPlaylist = (options: any) => {
        if (typeof options == "string") {
            const filterPlaylist = playlists.filter(
                (list: any) => list.id === options
            );
            if (filterPlaylist.length > 0) {
                return filterPlaylist[0].name + " - " + filterPlaylist[0].id;
            } else {
                return "";
            }
        } else if (typeof options == "object") {
            return options.name + " - " + options.id;
        }
    };

    return (
        <div className="config-heading">
            <div className="header-content-list">
                <div>
                    <h3>You May Also Like Rail</h3>
                </div>
                <div className="d-flex j-center">
                    {hasPermission(PERMISSIONS.curation, ACTION.EDIT) && (
                        <Button
                            className="float-right mb-2"
                            onClick={() => setShowCreateModal(true)}
                            variant="contained"
                        >
                            Create New Mapping
                        </Button>
                    )}
                </div>
            </div>
            <YmalTable status={LIVE} showActionButton={false} />
            <YmalTable
                ref={scheduleRef}
                status={SCHEDULED_STATUS}
                showActionButton={false}
            />

            {showCreateModal && (
                <Dialog
                    maxWidth="xl"
                    open={showCreateModal}
                    onClose={closeModal}
                >
                    <DialogTitle>
                        <Grid container width={800}>
                            <Grid item xs={4} md={4} lg={4}>
                                <span
                                    style={{
                                        fontSize: "large",
                                        alignSelf: "flex-start",
                                        fontWeight: "bold",
                                    }}
                                >
                                    #YouMayAlsoLike
                                </span>
                            </Grid>
                            <Grid item xs={5} md={5} lg={5}>
                                <span
                                    style={{
                                        fontSize: "large",
                                        alignSelf: "center",
                                        fontWeight: "bold",
                                    }}
                                >
                                    Create New Mapping
                                </span>
                            </Grid>
                        </Grid>
                    </DialogTitle>

                    <DialogContent>
                        <Grid container>
                            <Grid item xs={5} marginTop={2}>
                                <LocalizationProvider
                                    dateAdapter={AdapterDayjs}
                                >
                                    <DateTimePicker
                                        label="Publish Date & Time"
                                        sx={{ width: "100%" }}
                                        format={`MMM DD, YYYY hh:mm A`}
                                        value={
                                            (values.publishDateTime &&
                                                dayjs(
                                                    values.publishDateTime
                                                )) ||
                                            null
                                        }
                                        onChange={(e: any) => {
                                            setFieldValue("publishDateTime", e);
                                        }}
                                        closeOnSelect={false}
                                        disablePast
                                        slotProps={{
                                            textField: {
                                                size: "small",
                                                error: Boolean(
                                                    touched.publishDateTime &&
                                                        errors.publishDateTime
                                                ),
                                                helperText:
                                                    touched.publishDateTime &&
                                                    errors.publishDateTime,
                                                onBlur: handleBlur,
                                            },
                                            actionBar: {
                                                actions: ["clear", "accept"],
                                            },
                                            field: { clearable: true },
                                        }}
                                    />
                                </LocalizationProvider>
                            </Grid>
                            <Grid container item xs={12} marginTop={3}>
                                <Grid item xs={5}>
                                    <Autocomplete
                                        id="assetId"
                                        onBlur={handleBlur}
                                        options={assets}
                                        filterOptions={filterOptions}
                                        value={String(values.assetId)}
                                        includeInputInList
                                        onChange={(e, value) => {
                                            console.log("value", value);
                                            setFieldValue(
                                                "assetId",
                                                value?.id || ""
                                            );
                                            setFieldValue(
                                                "assetRef",
                                                value?.referenceId || ""
                                            );
                                            setFieldValue(
                                                "assetTitle",
                                                value?.name || ""
                                            );
                                            setFieldValue(
                                                "contentType",
                                                value?.contentType || ""
                                            );
                                            setFieldTouched("assetId", false);
                                        }}
                                        getOptionLabel={(options: any) => {
                                            return (
                                                renderOptionsInitial(options) ||
                                                ""
                                            );
                                        }}
                                        loading={videoAPILoader}
                                        size="small"
                                        renderInput={(params) => (
                                            <TextField
                                                name="assetId"
                                                label="Content ID"
                                                value={values.assetId}
                                                variant="outlined"
                                                helperText={
                                                    touched.assetId &&
                                                    errors.assetId
                                                }
                                                error={
                                                    touched.assetId &&
                                                    Boolean(errors.assetId)
                                                }
                                                {...params}
                                                InputProps={{
                                                    ...params.InputProps,
                                                    endAdornment: (
                                                        <React.Fragment>
                                                            {
                                                                params
                                                                    .InputProps
                                                                    .endAdornment
                                                            }
                                                            {videoAPILoader && (
                                                                <CircularProgress
                                                                    size={24}
                                                                />
                                                            )}
                                                        </React.Fragment>
                                                    ),
                                                }}
                                            />
                                        )}
                                    />
                                </Grid>
                                <Grid
                                    item
                                    xs={1}
                                    display={"flex"}
                                    justifyContent={"center"}
                                    alignItems={"center"}
                                >
                                    <EastIcon />
                                </Grid>
                                <Grid item xs={5}>
                                    <Autocomplete
                                        id="bcPlaylistId"
                                        options={playlists}
                                        filterOptions={filterOptions}
                                        value={String(values.playlistId)}
                                        includeInputInList
                                        size="small"
                                        onBlur={handleBlur}
                                        onChange={async (e, value) => {
                                            setFieldValue(
                                                "playlistId",
                                                value?.id || ""
                                            );
                                            setFieldValue(
                                                "playlistTitle",
                                                value?.name || ""
                                            );
                                            setFieldTouched(
                                                "playlistId",
                                                false
                                            );
                                        }}
                                        getOptionLabel={(options: any) => {
                                            return (
                                                renderOptionsInitialPlaylist(
                                                    options
                                                ) || ""
                                            );
                                        }}
                                        renderInput={(params) => (
                                            <TextField
                                                name="playlistId"
                                                label="Playlist ID"
                                                value={values.playlistId}
                                                variant="outlined"
                                                error={
                                                    touched.playlistId &&
                                                    Boolean(errors.playlistId)
                                                }
                                                helperText={
                                                    touched.playlistId &&
                                                    errors[`playlistId`]
                                                }
                                                {...params}
                                                InputProps={{
                                                    ...params.InputProps,
                                                    endAdornment: (
                                                        <React.Fragment>
                                                            {
                                                                params
                                                                    .InputProps
                                                                    .endAdornment
                                                            }
                                                            {playlistAPILoader && (
                                                                <CircularProgress
                                                                    size={24}
                                                                />
                                                            )}
                                                        </React.Fragment>
                                                    ),
                                                }}
                                            />
                                        )}
                                    />
                                </Grid>
                            </Grid>
                            <Grid
                                container
                                xs={12}
                                spacing={2}
                                marginTop={2}
                                marginLeft={1}
                            >
                                <Typography variant="body1">
                                    Content Type :{" "}
                                    {(values.contentType &&
                                        values?.contentType.toUpperCase()) ||
                                        "-"}
                                </Typography>
                            </Grid>
                        </Grid>
                    </DialogContent>
                    <DialogActions>
                        <Button
                            variant="contained"
                            color="warning"
                            onClick={closeModal}
                        >
                            Cancel
                        </Button>
                        <Button
                            startIcon={
                                saveLoader && (
                                    <CircularProgress
                                        style={{ color: "white" }}
                                        size={24}
                                    />
                                )
                            }
                            disabled={saveLoader}
                            variant="contained"
                            color="primary"
                            onClick={() => handleSubmit()}
                        >
                            Create
                        </Button>
                    </DialogActions>
                </Dialog>
            )}
        </div>
    );
};

export default ContentCurationVersions;
